import { Form, Input, message, Modal, Select, InputNumber, Row, Col, Typography, DatePicker, Switch, Divider } from "antd";
import TextArea from "antd/lib/input/TextArea";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { query_type, user_role } from "../consts";
import { useCreateQuery, useGetAllClients, useGetAllFinancialYears, useGetAllQueries, useGetAllPrimaryClientsUsers } from "../utils/queryUtils";
import { setReloadQuery } from "../store/action/reload";
import { useQueryClient } from "@tanstack/react-query";
import useFetch from "../_fetch";

var cc = require('currency-codes');

export default function CreateQuery({
    show, setShow, initialTypeValue = query_type.NORMAL,
}) {

    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const [selectedFinancialYearId, setSelectedFinancialYearId] = useState(null);
    const [isEntitySelected, setIsEntitySelected] = useState(false);
    const selectedPrimaryClient = useSelector((st) => st.selected.primaryClient || {});

    const [isTransactional, setIsTransactional] = useState(false);
    const [directAssign, setDirectAssign] = useState(false);
    const [selectedPrimaryClientIds, setSelectedPrimaryClientIds] = useState([]);

    const currencyCodes = cc.codes();
    let { role } = useSelector((st) => st.user);

    const currencyOptions = currencyCodes.map((code) => ({ label: code, value: code }));

    const layout = { labelCol: { span: 24 }, wrapperCol: { span: 24 } };
    const { Option } = Select;
    const _fetch = useFetch();

    const { name: selectedCompany } = useSelector(st => st.selected.company || {});
    const { company: companyObj } = useSelector(st => st.selected);
    const { client: selectedClient } = useSelector(st => st.selected);

    const all_clients_from_cache = useGetAllClients(selectedCompany);
    const all_clients = all_clients_from_cache?.data?.data;

    const { fy: selectedFy } = useSelector(st => st.selected);
    const [isFinancialYearSelected, setIsFinancialYearSelected] = useState(selectedFy?._id ? true : false);

    const [selectedEntity, setSelectedEntity] = useState((all_clients?.length > 0) ? all_clients[0] : null);
    useEffect(() => {
        if (all_clients?.length > 0) setSelectedEntity(all_clients[0]);
    }, [all_clients]);

    const data = useGetAllFinancialYears(selectedEntity || selectedClient?._id);
    const all_fys = data?.data;
    const queryData = useGetAllQueries(selectedFy?._id);
    const queries = queryData?.data?.data;
    const { mutateAsync: createQuery } = useCreateQuery();
    const queryClient = useQueryClient();

    const primaryClients = useGetAllPrimaryClientsUsers(companyObj?._id)?.data || [];

    const canDirectAssign =
        role === user_role.STAFF || role === user_role.ADMIN || role === user_role.ACCOUNTANT;

    const handleEntityChange = (selectedId) => {
        const selectedObject = all_clients.find(client => client._id === selectedId);
        if (selectedObject) {
            setSelectedEntity(selectedObject);
            data?.refetch();
            setIsEntitySelected(true);
            form.setFieldsValue({ financialYear: undefined });
        }
    };

    useEffect(() => { data.refetch(); }, [selectedEntity]);

    const handleFinancialYearChange = (value) => {
        setSelectedFinancialYearId(value);
        setIsFinancialYearSelected(true);
    };

    const resetModal = () => {
        form.resetFields();
        setIsTransactional(false);
        setDirectAssign(false);
        setSelectedPrimaryClientIds([]);
        setIsEntitySelected(false);
        setIsFinancialYearSelected(false);
    };

    async function submit(values) {
        if (directAssign && selectedPrimaryClientIds.length === 0) {
            message.error('Please select at least one primary client');
            return;
        }

        setLoading(true);
        try {
            if (values?.cashInValue >= 0 && values?.cashInCurrency) {
                values.cashIn = { value: values.cashInValue, currency: values.cashInCurrency };
                delete values.cashInValue;
                delete values.cashInCurrency;
            }
            if (values?.cashOutValue >= 0 && values?.cashOutCurrency) {
                values.cashOut = { value: values.cashOutValue, currency: values.cashOutCurrency };
                delete values.cashOutValue;
                delete values.cashOutCurrency;
            }

            const resolvedFyId = selectedFinancialYearId || selectedFy?._id;

            if (directAssign) {
                // Don't create a base query at all.
                // Send form data directly to assign_primary_client which creates
                // one copy per user — each copy belongs only to that client.
                const res = await _fetch(
                    `${process.env.REACT_APP_API_URL}/query/assign_primary_client`,
                    {
                        method: 'POST',
                        body: {
                            userIds: selectedPrimaryClientIds,
                            financialYear: resolvedFyId,
                            title: values.title,
                            descriptionV2: values.descriptionV2 ? [{ insert: values.descriptionV2 }] : [],
                            type: values.type || query_type.NORMAL,
                        },
                    }
                );
                const data = await res.json();
                if (data.success) {
                    const created = data.created_query_ids?.length || 0;
                    const skipped = data.skipped_user_ids?.length || 0;
                    if (created > 0 && skipped === 0) {
                        message.success(`Query sent directly to ${created} client(s)`);
                    } else if (created > 0 && skipped > 0) {
                        message.warning(`Sent to ${created} client(s), ${skipped} already had this query`);
                    } else {
                        message.warning('All selected clients already have this query');
                    }
                } else {
                    message.error(data.message || 'Failed to assign query');
                    return;
                }
            } else {
                // Original flow unchanged
                let type = user_role.COMPANY === role ? { type: "REVIEW_POINTS" } : null;
                const payload = {
                    ...values,
                    ...type,
                    financialYear: resolvedFyId,
                    descriptionV2: values.descriptionV2 ? [{ insert: values.descriptionV2 }] : [],
                    primaryClientId: selectedPrimaryClient?._id || null,
                };

                const queriesListKey = queryClient.getQueryCache().getAll();
                const pagination = queriesListKey
                    ?.filter((dat) => dat.queryKey[0] === 'queries')
                    .map(d => {
                        let p = d.queryKey[3];
                        if (d?.state?.data?.data?.length >= 10) p += 1;
                        return p;
                    })
                    .sort((a, b) => b - a);

                await createQuery({
                    ...payload,
                    cachefy: resolvedFyId,
                    cachetype: payload?.type || "NORMAL",
                    cachepage: pagination.length ? pagination[0] : 1,
                });

                user_role.COMPANY === role
                    ? message.success('Review Points created successfully')
                    : message.success('Query created successfully');
            }

            setShow(false);
            resetModal();
            dispatch(setReloadQuery(true));
        } catch (err) {
            console.error(err);
            message.error('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    }

    return (
        <Modal
            open={show}
            title={user_role.COMPANY === role ? "Create Review Points" : "Create Query"}
            destroyOnClose
            onCancel={() => { resetModal(); setShow(false); }}
            okText={
                directAssign && selectedPrimaryClientIds.length > 0
                    ? `Send to ${selectedPrimaryClientIds.length} Client(s)`
                    : user_role.COMPANY === role ? "Add Review Points" : 'Open Query'
            }
            onOk={() => {
                form.validateFields()
                    .then(values => { submit(values); form.resetFields(); setIsTransactional(false); })
                    .catch(info => console.error('Validate Failed:', info));
            }}
            okButtonProps={{ loading, htmlType: "submit" }}
            cancelButtonProps={{ loading }}
        >
            <Form
                layout="vertical"
                {...layout}
                onFinish={submit}
                form={form}
                initialValues={{
                    financialYear: selectedFy
                        ? `${moment(selectedFy?.start).format('YYYY')} - ${moment(selectedFy?.end).format('YY')}`
                        : undefined,
                    client_id: selectedClient?._id,
                }}
            >
                <Form.Item
                    label="Entity"
                    name="client_id"
                    initialValue={selectedClient || ((all_clients?.length > 0) ? all_clients[0]?.title : null)}
                >
                    <Select
                        value={selectedClient || ((all_clients?.length > 0) ? all_clients[0] : null)}
                        disabled={selectedClient?._id}
                        showSearch
                        optionFilterProp="label"
                        onChange={handleEntityChange}
                    >
                        {all_clients?.map(e => (
                            <Option value={e._id} key={e._id} label={e.title}>{e.title}</Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) => prevValues.client_id !== currentValues.client_id}
                >
                    {() => (
                        <Form.Item
                            label="Financial Year"
                            name="financialYear"
                            rules={[{ required: true, message: 'Financial Year is required' }]}
                        >
                            <Select onChange={handleFinancialYearChange} disabled={!isEntitySelected || selectedFy?._id}>
                                {all_fys?.data?.map((fy) => (
                                    <Option value={fy._id} key={fy._id}>
                                        <div style={{ display: 'flex', gap: '1rem', fontWeight: 500 }}>
                                            <span>{moment(fy.start).format('YYYY')} - {moment(fy.end).format('YY')}</span>
                                            | <span>{fy.fy_type}</span>
                                            | <span>{fy.period_type}</span>
                                        </div>
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}
                </Form.Item>

                {user_role.COMPANY !== role && (
                    <Form.Item
                        label="Query Type"
                        name="type"
                        initialValue={initialTypeValue}
                    >
                        <Select
                            value={initialTypeValue}
                            onChange={(val) => setIsTransactional(val === query_type.TRANSACTIONAL)}
                        >
                            <Option value={query_type.NORMAL}>Normal</Option>
                            <Option value={query_type.TRANSACTIONAL}>Transactional</Option>
                            <Option value={query_type.REVIEW_POINTS}>Review Points</Option>
                        </Select>
                    </Form.Item>
                )}

                {isTransactional && (
                    <div className='w-100'>
                        <Typography.Title level={5}>Transaction Details</Typography.Title>
                        <Row justify={'space-evenly'} gutter={[16, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item name='cashInValue' label='Cash In' initialValue={0}
                                    labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <InputNumber style={{ width: '200px' }} placeholder='Cash In' />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item name='cashInCurrency' label='Cash In Currency' initialValue={'AUD'}
                                    labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Select showSearch placeholder='Select a currency' options={currencyOptions} style={{ width: '200px' }} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row justify={'space-evenly'} gutter={[16, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item name='cashOutValue' label='Cash Out' initialValue={0}
                                    labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <InputNumber style={{ width: '200px' }} placeholder='Cash Out' />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item name='cashOutCurrency' label='Cash Out Currency' initialValue={'AUD'}
                                    labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Select showSearch placeholder='Select a currency' options={currencyOptions} style={{ width: '200px' }} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={24} md={12}>
                                <Form.Item name='Date' label='Date' initialValue={0}
                                    labelCol={{ span: 24 }} wrapperCol={{ span: 124 }}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <DatePicker format="YYYY-MM-DD" style={{ width: '200px' }} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item noStyle shouldUpdate>
                            {() => (
                                <Form.Item label="Parent Query" name="parent_id"
                                    rules={[{ required: true, message: 'Parent query is required' }]}>
                                    <Select disabled={!isFinancialYearSelected}>
                                        {queries?.map(e =>
                                            e.type === query_type.NORMAL
                                                ? <Option value={e._id} key={e._id}>{e.title}</Option>
                                                : null
                                        )}
                                    </Select>
                                </Form.Item>
                            )}
                        </Form.Item>
                    </div>
                )}

                <Form.Item label="Title" name="title" rules={[{ required: true, message: 'Query title is required' }]}>
                    <Input />
                </Form.Item>

                <Form.Item label="Description" name="descriptionV2">
                    <TextArea rows={5} />
                </Form.Item>

                {(canDirectAssign && user_role.STAFF === role && !selectedPrimaryClient?._id) && (
                    <>
                        <Divider style={{ margin: '12px 0' }} />
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: directAssign ? 12 : 0 }}>
                            <div>
                                <Typography.Text strong>Send directly to client</Typography.Text>
                                <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                                    Query goes straight to the selected client(s) — not visible to staff
                                </Typography.Text>
                            </div>
                            <Switch
                                checked={directAssign}
                                onChange={(checked) => {
                                    setDirectAssign(checked);
                                    if (!checked) setSelectedPrimaryClientIds([]);
                                }}
                            />
                        </div>

                        {(directAssign && user_role.STAFF === role && !selectedPrimaryClient?._id) && (
                            <Form.Item
                                label="Select Primary Client(s)"
                                style={{ marginTop: 12 }}
                                validateStatus={selectedPrimaryClientIds.length === 0 ? 'error' : ''}
                                help={selectedPrimaryClientIds.length === 0 ? 'Please select at least one client' : ''}
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="Search by name or email..."
                                    showSearch
                                    optionFilterProp="label"
                                    value={selectedPrimaryClientIds}
                                    onChange={setSelectedPrimaryClientIds}
                                    style={{ width: '100%' }}
                                    notFoundContent={
                                        <Typography.Text type="secondary">
                                            No primary clients found for this company
                                        </Typography.Text>
                                    }
                                >
                                    {primaryClients.map(u => (
                                        <Option
                                            key={u._id}
                                            value={u._id}
                                            label={`${u.first_name} ${u.last_name} ${u.email}`}
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.4 }}>
                                                <span style={{ fontWeight: 500 }}>{u.first_name} {u.last_name}</span>
                                                <span style={{ fontSize: 11, color: '#888' }}>{u.email}</span>
                                            </div>
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )}
                    </>
                )}
            </Form>
        </Modal>
    );
}